// État d'authentification, **en mémoire seulement**.
//
// Rien n'est écrit dans localStorage/sessionStorage, et c'est délibéré :
// l'`exportKey` (clé dérivée du mot de passe, racine du chiffrement local) ne
// doit jamais toucher le disque. Or persister le jeton de session sans elle
// donnerait une session à demi utilisable — connectée au serveur, mais
// incapable de déchiffrer quoi que ce soit. Un rafraîchissement de page
// redemande donc le mot de passe.
//
// À revoir si l'ergonomie l'exige : la piste propre est un cookie httpOnly pour
// la session + une re-dérivation de l'exportKey à la saisie du mot de passe.

import { create } from 'zustand';
import type { MasterKeyResult } from '../crypto/master-key';
import type { DeviceOutcome, DeviceSession, PendingDevice } from '../crypto/mls-state';
import type { Me, Session } from './opaque';

type AuthState = {
  session: Session | null;
  me: Me | null;
  /** Cabinet et e-mail : nécessaires pour retrouver l'appareil local. */
  tenantSlug: string;
  email: string;
  /**
   * Clé maîtresse K, déverrouillée à la connexion. Racine de tout le
   * chiffrement local — en mémoire uniquement, jamais persistée.
   */
  masterKey: MasterKeyResult | null;
  /** Identité MLS de cet appareil, restaurée depuis son état chiffré. */
  device: DeviceSession | null;
  /**
   * Appareil en attente d'approbation : il a demandé, il n'a le droit de rien.
   * Le propriétaire doit comparer son empreinte depuis son appareil principal.
   */
  pendingDevice: PendingDevice | null;
  signIn: (
    session: Session,
    me: Me,
    masterKey: MasterKeyResult,
    outcome: DeviceOutcome,
    identity: { tenantSlug: string; email: string },
  ) => void;
  /** L'appareil vient d'être approuvé : il devient pleinement opérationnel. */
  setDevice: (device: DeviceSession) => void;
  /** Le second facteur vient d'être activé ou levé. */
  setTwoFactor: (enabled: boolean) => void;
  /**
   * Adopte le nouvel `exportKey` après un changement de mot de passe : l'ancien
   * ne dérive plus la bonne KEK. K, elle, ne change pas.
   */
  setExportKey: (exportKey: string) => void;
  signOut: () => void;
};

export const useAuth = create<AuthState>((set) => ({
  session: null,
  me: null,
  tenantSlug: '',
  email: '',
  masterKey: null,
  device: null,
  pendingDevice: null,
  signIn: (session, me, masterKey, outcome, identity) =>
    set({
      session,
      me,
      masterKey,
      tenantSlug: identity.tenantSlug,
      email: identity.email,
      device: outcome.kind === 'ready' ? outcome.device : null,
      pendingDevice: outcome.kind === 'pending' ? outcome.device : null,
    }),
  setDevice: (device) => set({ device, pendingDevice: null }),
  setTwoFactor: (enabled) =>
    set((s) => (s.me ? { me: { ...s.me, twoFactorEnabled: enabled } } : s)),
  setExportKey: (exportKey) =>
    set((s) => (s.session ? { session: { ...s.session, exportKey } } : s)),
  signOut: () =>
    set({
      session: null,
      me: null,
      tenantSlug: '',
      email: '',
      masterKey: null,
      device: null,
      pendingDevice: null,
    }),
}));
